import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Star, Plus, Trash2, Save, Loader2, BarChart3, ExternalLink, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewsAdminPage = () => {
  const { user } = useAuth();
  const [pills, setPills] = useState([]);
  const [googleUrl, setGoogleUrl] = useState('');
  const [stats, setStats] = useState({ total_generated: 0, per_pill: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/reviews/config`),
        axios.get(`${API}/reviews/stats`),
      ]);
      setPills(c.data.pills || []);
      setGoogleUrl(c.data.google_business_url || '');
      setStats(s.data);
    } catch (err) {
      console.error('[ReviewsAdmin] load failed:', err);
    }
    setLoading(false);
    setDirty(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updatePill = (idx, v) => { setPills((p) => p.map((x, i) => (i === idx ? v : x))); setDirty(true); };
  const addPill = () => { if (pills.length >= 20) return toast.error('Max 20 pills'); setPills([...pills, '']); setDirty(true); };
  const removePill = (i) => { setPills(pills.filter((_, idx) => idx !== i)); setDirty(true); };

  const save = async ({ silent = false } = {}) => {
    setSaving(true);
    try {
      const cleaned = pills.map((p) => p.trim()).filter(Boolean);
      const r = await axios.put(`${API}/reviews/config`, { pills: cleaned, google_business_url: googleUrl });
      setPills(r.data.pills || []);
      setGoogleUrl(r.data.google_business_url || '');
      setDirty(false);
      if (!silent) toast.success(`Saved ${cleaned.length} pill${cleaned.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  // Count of pills that actually have content (what will end up saved)
  const filledCount = pills.filter((p) => p && p.trim()).length;

  const LayoutWrapper = user?.role === 'superadmin' ? AdminLayout : Layout;
  const maxCount = Math.max(1, ...stats.per_pill.map((p) => p.count));

  if (loading) {
    return <LayoutWrapper><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" /></div></LayoutWrapper>;
  }

  return (
    <LayoutWrapper>
      <div className="p-4 lg:p-8 pb-24 max-w-5xl mx-auto" data-testid="reviews-admin-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6 lg:mb-8">
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6 lg:w-7 lg:h-7 text-[#FFD700]" />
            <div>
              <h1 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight font-['Outfit']">Reviews</h1>
              <p className="text-gray-400 text-xs lg:text-sm">AI-written Google reviews from your guests.</p>
            </div>
          </div>
          {dirty && (
            <Button onClick={save} disabled={saving} className="bg-[#FFD700] hover:bg-[#e6c200] text-black rounded-full px-5 text-xs font-bold tracking-wider" data-testid="reviews-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} SAVE
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Pills */}
          <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-5 lg:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-extrabold text-white uppercase tracking-[0.18em] font-['Outfit']">
                Experience pills <span className="text-gray-600 normal-case ml-1">({pills.length}/20)</span>
              </h3>
              <Button onClick={addPill} variant="ghost" className="text-[#FFD700] hover:bg-[#FFD700]/10 h-7 px-2.5 rounded-full text-[10px] font-bold tracking-wider" data-testid="reviews-add-pill">
                <Plus className="w-3 h-3 mr-1" /> ADD
              </Button>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">Guests will pick from these as multi-select pills.</p>
            <div className="space-y-2">
              {pills.map((p, idx) => (
                // Key MUST be stable across keystrokes — using the pill text
                // (or any derivative of it) causes React to unmount and
                // remount this row on every character typed, dropping the
                // Input's focus. Since pills are edited in-place by index
                // (see updatePill), the array index is the correct stable key.
                <div key={`pill-row-${idx}`} className="flex gap-2" data-testid={`reviews-pill-row-${idx}`}>
                  <Sparkles className="w-3.5 h-3.5 text-[#FFD700] mt-3 flex-shrink-0" />
                  <Input
                    value={p}
                    onChange={(e) => updatePill(idx, e.target.value.slice(0, 80))}
                    onBlur={() => { if (dirty && (p || '').trim()) save({ silent: true }); }}
                    placeholder="Great drinks"
                    className="bg-black/40 border-white/10 text-white h-10 rounded-xl flex-1"
                    data-testid={`reviews-pill-input-${idx}`}
                  />
                  <Button onClick={() => removePill(idx)} variant="ghost" className="h-10 w-10 p-0 rounded-xl text-[#FF3B3B] hover:bg-[#FF3B3B]/10" data-testid={`reviews-pill-remove-${idx}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-white/5">
              <label className="text-xs text-gray-400 mb-2 block font-semibold tracking-wider uppercase">Google Business URL</label>
              <Input
                value={googleUrl}
                onChange={(e) => { setGoogleUrl(e.target.value); setDirty(true); }}
                onBlur={() => { if (dirty) save({ silent: true }); }}
                placeholder="https://g.page/your-venue/review"
                className="bg-black/40 border-white/10 text-white h-10 rounded-xl"
                data-testid="reviews-google-url"
              />
              <p className="text-[11px] text-gray-500 mt-2">Customers tap &quot;Paste on Google&quot; → opens this URL in a new tab.</p>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-5 lg:p-6" data-testid="reviews-stats-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#39FF14]" />
                <h3 className="text-sm font-extrabold text-white uppercase tracking-[0.18em] font-['Outfit']">Stats</h3>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#39FF14]/15 text-[#39FF14]" data-testid="reviews-total-count">
                {stats.total_generated} generated
              </span>
            </div>
            {stats.per_pill.length === 0 ? (
              <p className="text-gray-500 text-xs text-center py-12">No reviews yet. Once guests start using it, stats will appear here.</p>
            ) : (
              <div className="space-y-3">
                {stats.per_pill.map((row) => (
                  <div key={row.pill} data-testid={`reviews-stat-${row.pill}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-sm font-semibold truncate">{row.pill}</span>
                      <span className="text-[#39FF14] text-xs font-bold font-mono">{row.count}</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#FFD700] to-[#39FF14] rounded-full transition-all"
                        style={{ width: `${(row.count / maxCount) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {googleUrl && (
              <a
                href={googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 flex items-center gap-2 text-xs text-[#FFD700] hover:underline pt-5 border-t border-white/5"
                data-testid="reviews-open-google"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open Google Business page
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Sticky save bar — always visible when dirty so changes are never lost */}
      {dirty && (
        <div
          className="fixed bottom-0 inset-x-0 z-50 bg-black/85 backdrop-blur-xl border-t border-[#FFD700]/30 shadow-[0_-10px_40px_rgba(255,215,0,0.15)]"
          data-testid="reviews-sticky-save-bar"
        >
          <div className="max-w-5xl mx-auto px-4 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-[#FFD700] animate-pulse flex-shrink-0" />
              <span className="text-white text-xs font-semibold font-['Outfit'] truncate">
                You have unsaved changes
                <span className="text-gray-500 ml-2 hidden sm:inline">
                  ({filledCount} pill{filledCount === 1 ? '' : 's'} will be saved)
                </span>
              </span>
            </div>
            <Button
              onClick={() => save()}
              disabled={saving}
              className="bg-[#FFD700] hover:bg-[#e6c200] text-black rounded-full px-5 h-9 text-xs font-bold tracking-wider flex-shrink-0"
              data-testid="reviews-save-now"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SAVING</> : <><Save className="w-4 h-4 mr-1.5" /> SAVE NOW</>}
            </Button>
          </div>
        </div>
      )}
    </LayoutWrapper>
  );
};

export default ReviewsAdminPage;
